import React, { useEffect, useRef, useState } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import Loader from "../../common/Loader";
import useStore from "../../../context/useStore";
import tools from "../../../helpers/tools";
import dataService from "../../../helpers/dataService";
import AnswerInterpretor, { EMAIL_SENTINEL } from "./AnswerInterpretor";
import ProgressBar from "./ProgressBar";
import LoaderFinish from "./LoaderFinish";
import LoadLSOverlay from "./LoadLSOverlay";
import {
  applyAnswer,
  buildSubmitPayload,
  findAnswer,
  initialAnswers,
  isAnswerEmpty,
  resolveStep,
  validationUrl,
} from "./formLogic";
import "./form-generator.scss";

export const REQUIRED_MESSAGE = "Veuillez répondre à cette question pour continuer.";

/*
  Moteur de formulaire piloté par un schéma distant (`formDataUrl`), une
  question à la fois. Contrat API inchangé : GET du schéma, POST de validation
  par étape (`.../validation/<étape>`), POST d'envoi final.

  Refonte : une seule question montée (plus de piège de tabulation), intitulé
  relié au contrôle, erreurs annoncées et rattachées au champ, focus déplacé
  sur la nouvelle question, réponse obligatoire vérifiée avant l'appel serveur
  (sauf `isNotRequired`), progression lisible.
*/
const FormGenerator = ({
  formDataUrl,
  formValidationUrl = null,
  formSubmitUrl = null,
  setIsDspFinished,
  setFormData,
  withContinue = false,
  formatSaveData = false,
  closingPhrase = "Veuillez patienter pendant que nous déterminons votre orientation.",
  successPhrase = "",
  additionalDataToSave = false,
  specialCaseRDSP = false,
  setRdspForm = () => {},
  checkEmail = false,
}) => {
  const [state] = useStore();
  const inputTypes = (state.constants.items && state.constants.items.INPUT_TYPES) || [];
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [oldEmail, setOldEmail] = useState("");
  const containerRef = useRef(null);
  const focusPendingRef = useRef(false);
  const inputTypesRef = useRef(inputTypes);
  inputTypesRef.current = inputTypes;

  useEffect(() => {
    if (!formDataUrl) return;
    Axios.get(formDataUrl)
      .then((res) => {
        setForm(res.data);
        setRdspForm(res.data);
        setAnswers(initialAnswers(res.data.questions, inputTypesRef.current));
      })
      .catch(() => {});
  }, [formDataUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = form ? form.questions[step] : null;
  const isLastStep = form ? step === form.questions.length - 1 : false;

  // À chaque changement d'étape : sauvegarde locale éventuelle, vérification de
  // l'email connu, remontée en haut du formulaire et focus sur le contrôle.
  useEffect(() => {
    if (!form || !answers) return;
    if (step > 0 && withContinue) {
      tools.saveState("FormRDSP", "answers", answers);
      tools.saveState("FormRDSP", "step", step);
    }
    if (checkEmail && current && current.attribute === "email") {
      checkEmailExists();
    }
    if (focusPendingRef.current && containerRef.current) {
      focusPendingRef.current = false;
      if (step > 0 && containerRef.current.scrollIntoView) {
        containerRef.current.scrollIntoView({ block: "start" });
      }
      const target = containerRef.current.querySelector(
        ".question_container input:not([type=hidden]):not([disabled]), .question_container select, .question_container textarea, .question_container button.btn-input"
      );
      if (target) target.focus();
    }
  }, [step, form, answers]); // eslint-disable-line react-hooks/exhaustive-deps

  function goTo(target, direction, sourceAnswers = answers) {
    const resolved = resolveStep(form.questions, sourceAnswers, target, direction);
    if (resolved.submit) {
      save(sourceAnswers);
      return;
    }
    focusPendingRef.current = true;
    setStep(resolved.step);
  }

  function updateAnswer(e) {
    // Photographie de l'événement : React remet la valeur contrôlée du champ
    // avant d'appliquer la mise à jour d'état, `e.target.value` serait déjà vide.
    const target = {
      name: e.target.name,
      value: e.target.value,
      isExclusive: e.target.isExclusive,
      override: e.target.override,
      formArray: e.target.formArray
        ? { target: { checked: e.target.formArray.target.checked } }
        : undefined,
    };
    setAnswers((prev) => applyAnswer(prev, current, target, inputTypes));
    setErrors((prev) => {
      if (!(current.id in prev) && !(String(current.id) in prev)) return prev;
      const next = { ...prev };
      delete next[current.id];
      delete next[String(current.id)];
      return next;
    });
  }

  function prevStep() {
    setErrors({});
    goTo(step - 1, "backward");
  }

  function nextStep() {
    validateStep(false);
  }

  function validateStep(isLast, datas = null) {
    const payload = datas || answers;
    if (!datas && !current.isNotRequired && isAnswerEmpty(findAnswer(payload, current.id))) {
      setErrors({ [current.id]: REQUIRED_MESSAGE });
      return;
    }
    setErrors({});
    setIsValidating(true);
    Axios.post(validationUrl({ formValidationUrl, formDataUrl }, step), payload)
      .then(() => {
        setIsValidating(false);
        if (isLast) {
          save(payload);
        } else {
          goTo(step + 1, "forward", payload);
        }
      })
      .catch((err) => {
        setIsValidating(false);
        if (err.response) {
          setErrors(err.response.data || {});
        }
      });
  }

  function save(payload = answers) {
    setIsSaving(true);
    const datas = buildSubmitPayload(form, payload, {
      formatSaveData,
      additionalDataToSave,
      specialCaseRDSP,
    });
    Axios.post(formSubmitUrl ? formSubmitUrl : formDataUrl, datas)
      .then((res) => {
        setFormData(res.data);
        setIsDspFinished(true);
        if (successPhrase !== "") {
          toast.success(successPhrase);
        }
      })
      .catch((err) => {
        if (err.response) setErrors(err.response.data || {});
        setIsSaving(false);
      });
  }

  function checkEmailExists() {
    dataService.post("recipients_dsp/exist", { nir: additionalDataToSave.nir }, (datas) => {
      setOldEmail(datas.exist || "");
    });
  }

  function replaceWithOldEmail() {
    const next = answers.map((a) =>
      String(a.question) === String(current.id) ? { ...a, value: EMAIL_SENTINEL } : a
    );
    setAnswers(next);
    validateStep(false, next);
  }

  const isReady = form && answers && inputTypes.length > 0;
  const currentAnswer = isReady ? findAnswer(answers, current.id) : null;
  const otherErrors = Object.entries(errors).filter(
    ([key]) => !current || String(key) !== String(current.id)
  );

  return (
    <>
      {withContinue && <LoadLSOverlay setAnswers={setAnswers} setStep={setStep} />}
      <div
        id="form_container"
        ref={containerRef}
        className="form-generateur"
      >
        {!isReady ? (
          <Loader />
        ) : isSaving ? (
          <LoaderFinish text={closingPhrase} />
        ) : (
          <>
            <h2 id="form_section_title" className="question_section_title">
              {form.sections[current.section]}
            </h2>
            <ProgressBar current={step + 1} total={form.questions.length} />

            <div key={"q" + current.id} className="question_container animated fadeIn faster">
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  nextStep();
                }}
              >
                <div className="question_answer_container">
                  <AnswerInterpretor
                    question={current}
                    inputTypes={inputTypes}
                    updateAnswer={updateAnswer}
                    answer={currentAnswer}
                    errors={errors}
                  />
                </div>

                {current.attribute === "email" && (
                  <div className="question_email_help">
                    {checkEmail && isAnswerEmpty(currentAnswer) && oldEmail !== "" && (
                      <div className="p-3 border mb-2 rounded alert alert-info d-center flex-column">
                        <p>Une adresse email vous concernant est déjà enregistrée à la Caf.</p>
                        <p className="text-center">
                          Adresse masquée : <strong>{oldEmail}</strong>
                        </p>
                        <p>Souhaitez-vous l'utiliser ou en déclarer une différente ?</p>
                        <button type="button" className="btn btn-light" onClick={replaceWithOldEmail}>
                          Utiliser mon email déjà enregistrée
                        </button>
                      </div>
                    )}
                    <p>
                      Vous ne possédez pas encore de boite email ? Utilisez ce lien pour en
                      créer une :
                    </p>
                    <ul>
                      <li>
                        <a target="_blank" rel="noopener noreferrer" href="https://www.laposte.net/accueil">
                          Créer une boite email sur LA POSTE
                          <span className="sr-only"> (nouvelle fenêtre)</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                )}

                <div className="question_navigation">
                  {step > 0 ? (
                    <button type="button" className="btn btn-ghost btn_nav btn_nav_prev" onClick={prevStep}>
                      <i className="fa fa-chevron-left mr-2" aria-hidden="true"></i>
                      Retour
                    </button>
                  ) : (
                    <span />
                  )}
                  {!isLastStep && (
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg btn_nav btn_nav_next"
                      disabled={isValidating || isSaving}
                      aria-busy={isValidating ? "true" : undefined}
                    >
                      {isValidating ? "Vérification…" : "Continuer"}
                    </button>
                  )}
                  {isLastStep && (
                    <button
                      type="button"
                      className="btn btn-primary btn-lg btn_nav btn_send"
                      disabled={isValidating || isSaving}
                      aria-busy={isValidating ? "true" : undefined}
                      onClick={() => validateStep(true)}
                    >
                      <i className="fa fa-check mr-2" aria-hidden="true"></i>
                      Envoyer mes réponses
                    </button>
                  )}
                </div>
              </form>

              <div className="form_errors">
                {otherErrors.map(([key, err]) => (
                  <div key={"err" + key} role="alert" className="alert alert-danger mt-2">
                    {err}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default FormGenerator;
